import { useEffect, useState } from 'react';

const BOOTSTRAP_SCRIPT_ID = 'google-maps-bootstrap-loader';

/**
 * Bootstrap loader de Google Maps que expone importLibrary.
 * Ver: https://developers.google.com/maps/documentation/javascript/load-maps-js-api#dynamic-library-import
 *
 * No aplica la restricción de tipos de Places (AVISOS-6014): eso vive en autocomplete/detalle
 * (`useGoogleMapsPlacesBff` vía BFF, o código deprecado en `useGoogleMapsAPI`).
 */
function injectBootstrapLoader(apiKey) {
  if (typeof document === 'undefined' || !apiKey) return;
  if (document.getElementById(BOOTSTRAP_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = BOOTSTRAP_SCRIPT_ID;
  script.textContent = `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src="https://maps."+c+"apis.com/maps/api/js?"+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({key:"${apiKey}",v:"weekly"});`;
  document.head.appendChild(script);
}

/**
 * Hook que carga Google Maps usando el bootstrap loader (importLibrary).
 * Reemplaza useLoadScript para habilitar Places API (New).
 *
 * @param {Object} options
 * @param {string} options.googleMapsApiKey - API key de Google Maps
 * @returns {{ isLoaded: boolean, loadError: Error|null }}
 */
export function useGoogleMapsBootstrap({ googleMapsApiKey } = {}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const apiKey = googleMapsApiKey || process.env.REACT_APP_GOOGLE_MAP_KEY;

  useEffect(() => {
    if (!apiKey) {
      setLoadError(new Error('Google Maps API key no configurada'));
      return;
    }

    let cancelled = false;

    async function load() {
      injectBootstrapLoader(apiKey);

      const waitForImportLibrary = () =>
        new Promise(resolve => {
          const check = () => {
            if (cancelled) return;
            if (typeof window !== 'undefined' && window.google?.maps?.importLibrary) {
              resolve(true);
              return;
            }
            setTimeout(check, 50);
          };
          check();
        });

      try {
        await waitForImportLibrary();
        if (cancelled) return;

        await window.google.maps.importLibrary('maps');
        if (cancelled) return;

        await window.google.maps.importLibrary('places');
        if (cancelled) return;

        setIsLoaded(true);
        setLoadError(null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return { isLoaded, loadError };
}
